import React from 'react';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { IconLoader } from 'common/IconLoader';
import {
  doDeleteFactoryDevices,
  doDeleteProtectedDevices,
  doAddProtectedDevices,
  doAddFactoryDevices
} from 'redux/actions';
import {
  capitalizeFirstLetter,
  deviceToProductTypeDisplayString,
  getDeviceSwVersion,
  getMainUnitFromBundleMembers,
  getMaxAlarmForDevices,
  getMaxAlarmFromDevice
} from 'common/utils/helpers';
import {
  getDeviceLastActivityTime,
  getGenFromProductType,
  getModelFromProductType,
  isItBundle
} from 'common/utils/deviceTwinUtils';

export const getDeviceGains = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  const DLH = mainDevice?.properties?.reported?.['HDL_Gain']?.value || '00';
  const DLV = mainDevice?.properties?.reported?.['VDL_Gain']?.value || '00';
  const ULH = mainDevice?.properties?.reported?.['HUL_Gain']?.value || '00';
  const ULV = mainDevice?.properties?.reported?.['VUL_Gain']?.value || '00';

  // return DLH + '/' + DLV + '/' + ULH + '/' + ULV;
  return {
    dlh: DLH,
    dlv: DLV,
    ulh: ULH,
    ulv: ULV
  };
};

export const PROTECTED_FACTORY_STATE = {
  unknown: 0,
  // protected state
  protectedWithPermission: 1,
  protectedWithoutPermission: 2,
  unprotectedWithPermission: 3,
  unprotectedWithoutPermission: 4,
  // factory state
  factoryProtectedWithPermission: 5,
  factoryProtectedWithoutPermission: 6,
  factoryUnprotectedWithPermission: 7,
  factoryUnprotectedWithoutPermission: 8,
  // bundle protected state
  bundleProtectedWithPermission: 9,
  bundlePartiallyProtectedWithPermission: 10,
  bundleUnprotectedWithPermission: 11,
  bundleProtectedWithoutPermission: 12,
  bundlePartiallyProtectedWithoutPermission: 13,
  bundleUnprotectedWithoutPermission: 14,
  // bundle factory state
  bundleFactoryProtectedWithPermission: 15,
  bundleFactoryPartiallyProtectedWithPermission: 16,
  bundleFactoryUnprotectedWithPermission: 17,
  bundleFactoryProtectedWithoutPermission: 18,
  bundleFactoryPartiallyProtectedWithoutPermission: 19,
  bundleFactoryUnprotectedWithoutPermission: 20
};

export const canReadProtectedState = (userPermissions) => {
  const SW_PROTECTED_DEVICE_READ = 'sw_protected_device_read';
  if (!userPermissions) return false;

  return userPermissions.includes(SW_PROTECTED_DEVICE_READ);
};

export const canModifyProtectedState = (userPermissions) => {
  const SW_PROTECTED_DEVICE_WRITE = 'sw_protected_device_write';
  const SW_PROTECTED_DEVICE_DELETE = 'sw_protected_device_delete';
  if (!userPermissions) return false;

  return userPermissions.includes(SW_PROTECTED_DEVICE_WRITE) && userPermissions.includes(SW_PROTECTED_DEVICE_DELETE);
};

export const canReadFactoryState = (userPermissions) => {
  const SW_FACTORY_DEVICE_READ = 'sw_factory_device_read';
  if (!userPermissions) return false;

  return userPermissions.includes(SW_FACTORY_DEVICE_READ);
};

export const canModifyFactoryState = (userPermissions) => {
  const SW_FACTORY_DEVICE_WRITE = 'sw_factory_device_write';
  const SW_FACTORY_DEVICE_DELETE = 'sw_factory_device_delete';
  if (!userPermissions) return false;

  return userPermissions.includes(SW_FACTORY_DEVICE_WRITE) && userPermissions.includes(SW_FACTORY_DEVICE_DELETE);
};

export const getDeviceProtectedState = (isProtected, hasPermission) => {
  if (isProtected && hasPermission) {
    return PROTECTED_FACTORY_STATE.protectedWithPermission;
  } else if (isProtected && !hasPermission) {
    return PROTECTED_FACTORY_STATE.protectedWithoutPermission;
  } else if (!isProtected && hasPermission) {
    return PROTECTED_FACTORY_STATE.unprotectedWithPermission;
  } else {
    return PROTECTED_FACTORY_STATE.unprotectedWithoutPermission;
  }
};

export const getDeviceFactoryState = (isProtected, hasPermission) => {
  if (isProtected && hasPermission) {
    return PROTECTED_FACTORY_STATE.factoryProtectedWithPermission;
  } else if (isProtected && !hasPermission) {
    return PROTECTED_FACTORY_STATE.factoryProtectedWithoutPermission;
  } else if (!isProtected && hasPermission) {
    return PROTECTED_FACTORY_STATE.factoryUnprotectedWithPermission;
  } else {
    return PROTECTED_FACTORY_STATE.factoryUnprotectedWithoutPermission;
  }
};

export const getBundleProtectedState = (membersProtected, hasPermission) => {
  if (!membersProtected) return;
  if (membersProtected.every((member) => member === true) && hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleProtectedWithPermission;
  }
  if (membersProtected.every((member) => member === true) && !hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleProtectedWithoutPermission;
  }
  if (membersProtected.some((member) => member === true) && hasPermission) {
    return PROTECTED_FACTORY_STATE.bundlePartiallyProtectedWithPermission;
  }
  if (membersProtected.some((member) => member === true) && !hasPermission) {
    return PROTECTED_FACTORY_STATE.bundlePartiallyProtectedWithoutPermission;
  }
  if (hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleUnprotectedWithPermission;
  }
  if (!hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleUnprotectedWithoutPermission;
  }
};

export const getBundleFactoryState = (membersProtected, hasPermission) => {
  if (!membersProtected) return;
  if (membersProtected.every((member) => member === true) && hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleFactoryProtectedWithPermission;
  }
  if (membersProtected.every((member) => member === true) && !hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleFactoryProtectedWithoutPermission;
  }
  if (membersProtected.some((member) => member === true) && hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleFactoryPartiallyProtectedWithPermission;
  }
  if (membersProtected.some((member) => member === true) && !hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleFactoryPartiallyProtectedWithoutPermission;
  }
  if (hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleFactoryUnprotectedWithPermission;
  }
  if (!hasPermission) {
    return PROTECTED_FACTORY_STATE.bundleFactoryUnprotectedWithoutPermission;
  }
};

export const getProtectedFactoryDiv = (deviceIds, state, dispatch) => {
  switch (state) {
    case PROTECTED_FACTORY_STATE.unknown:
      return <div className="DeviceTable_FactoryProtectedDiv_NoPermission">Unknown</div>;
    case PROTECTED_FACTORY_STATE.protectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doDeleteProtectedDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_checked_with_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.protectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_checked_without_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.unprotectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doAddProtectedDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_unchecked_with_permission} tooltip="unprotected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.unprotectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_unchecked_without_permission} tooltip="unprotected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.factoryProtectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doDeleteFactoryDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_checked_with_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.factoryProtectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_checked_without_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.factoryUnprotectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doAddFactoryDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_unchecked_with_permission} tooltip="unprotected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.factoryUnprotectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_unchecked_without_permission} tooltip="unprotected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleProtectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doDeleteProtectedDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_checked_with_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundlePartiallyProtectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doDeleteProtectedDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_partially_checked_with_permission} tooltip="partially protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleUnprotectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doAddProtectedDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_unchecked_with_permission} tooltip="unprotected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleProtectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_checked_without_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundlePartiallyProtectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_partially_checked_without_permission} tooltip="partially protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleUnprotectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_unchecked_without_permission} tooltip="unprotected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleFactoryProtectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doDeleteFactoryDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_checked_with_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleFactoryPartiallyProtectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doDeleteFactoryDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_partially_checked_with_permission} tooltip="partially protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleFactoryUnprotectedWithPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv" onClick={() => dispatch(doAddFactoryDevices(deviceIds))}>
          <SvgAF img={IconLoader.icon_unchecked_with_permission} tooltip="unprotected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleFactoryProtectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_checked_without_permission} tooltip="protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleFactoryPartiallyProtectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_partially_checked_without_permission} tooltip="partially protected" />
        </div>
      );
    case PROTECTED_FACTORY_STATE.bundleFactoryUnprotectedWithoutPermission:
      return (
        <div className="DeviceTable_FactoryProtectedDiv_NoPermission">
          <SvgAF img={IconLoader.icon_unchecked_without_permission} tooltip="unprotected" />
        </div>
      );
    default:
      return <div className="DeviceTable_FactoryProtectedDiv_NoPermission">Unknown</div>;
  }
};

export const isResizable = (columns, currentColId) => {
  if (currentColId === 'device_id_col') return true;
  let resizable = true;
  let lastColId = columns?.[columns?.length - 1].id;
  if (lastColId === currentColId) resizable = false;
  return resizable;
};

export const reformatUserPreferencesArrIntoObj = (userPreferences) => {
  const obj = {};
  if (!Array.isArray(userPreferences)) return obj;
  userPreferences.forEach((preference) => {
    obj[preference.id] = preference;
  });
  return obj;
};

export const swVersionColAccessor = (device) => {
  if (!isItBundle(device)) return getDeviceSwVersion(device);

  let bundleMembers = device.members;
  let mainDevice = getMainUnitFromBundleMembers(bundleMembers);
  let lowestSwVersion = getDeviceSwVersion(mainDevice);

  bundleMembers &&
    bundleMembers.forEach((member) => {
      let curVersion = getDeviceSwVersion(member);
      if (curVersion.toLowerCase() !== 'unknown' && lowestSwVersion > curVersion) lowestSwVersion = curVersion;
    });

  return lowestSwVersion;
};

export const connectionStatusColAccessor = (device) => {
  let connectionStatus = 'Online';
  let lastActivityTime = 'Unknown';

  if (!isItBundle(device)) {
    connectionStatus = device?.connectionState === 'Connected' ? 'Online' : 'Offline';
    lastActivityTime = getDeviceLastActivityTime(device);
  } else {
    const disconnectedMember = device.members.find((member) => member?.connectionState === 'Disconnected');
    if (disconnectedMember) {
      connectionStatus = 'Offline';
      lastActivityTime = getDeviceLastActivityTime(disconnectedMember);
    }
  }

  return { connectionStatus, lastActivityTime };
};

export const genModelColAccessor = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  const productType = mainDevice?.properties?.reported?.productType;
  const genType = getGenFromProductType(productType);
  const modelType = getModelFromProductType(productType);
  let displayString = 'Unknown';
  if (genType !== 'Unknown') {
    displayString = genType;
  } else if (modelType !== 'Unknown') {
    displayString = modelType;
  }
  return displayString;
};

export const wifiColAccessor = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  const wifiStatus = mainDevice?.properties?.reported?.wifi?.status ?? 'Unknown';
  return typeof wifiStatus === 'string' ? capitalizeFirstLetter(wifiStatus) : 'Unknown';
};

export const deviceTypeColAccessor = (device) => {
  return isItBundle(device) ? device.type : deviceToProductTypeDisplayString(device);
};

export const lockedUnlockedColAccessor = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  const lockStatus = mainDevice?.properties?.reported?.adminState ?? 'Unknown';
  return typeof lockStatus === 'string' ? capitalizeFirstLetter(lockStatus) : 'Unknown';
};

export const modelColAccessor = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  const productType = mainDevice?.properties?.reported?.productType;
  return getModelFromProductType(productType);
};

export const genColAccessor = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  const productType = mainDevice?.properties?.reported?.productType;
  const genType = getGenFromProductType(productType);
  return genType;
};

export const alarmsColAccessor = (device) => {
  let maxAlarm = null;
  const isBundle = isItBundle(device);
  if (isBundle) {
    maxAlarm = getMaxAlarmForDevices(device?.members);
  } else {
    if (device?.tags != null) maxAlarm = getMaxAlarmFromDevice(device);
  }
  return maxAlarm;
};

export const deviceIdColAccessor = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  return mainDevice?.id || mainDevice?.deviceId;
};

export const protectedColAccessor = (device, protectedList, userPermissions) => {
  if (device.bundleId !== undefined) {
    const memberIds = device.members.map((member) => member.deviceId);
    const protectedIds = protectedList && protectedList.map((protectedDevice) => protectedDevice.deviceId);
    let isMemberProtected = memberIds && protectedIds && memberIds.map((memberId) => protectedIds.includes(memberId));
    const canModifyProtectedDevices = canModifyProtectedState(userPermissions);
    return {
      bundleId: device.bundleId,
      memberIds,
      bundleState: getBundleProtectedState(isMemberProtected, canModifyProtectedDevices),
      memberStates:
        isMemberProtected &&
        isMemberProtected.map((isProtected) => getDeviceProtectedState(isProtected, canModifyProtectedDevices))
    };
  } else {
    const isProtected =
      protectedList && protectedList.some((protectedDevice) => protectedDevice.deviceId === device.id);
    const canModifyProtectedDevices = canModifyProtectedState(userPermissions);
    return { deviceId: device.id, state: getDeviceProtectedState(isProtected, canModifyProtectedDevices) };
  }
};

export const factoryColAccessor = (device, factoryList, userPermissions) => {
  if (device.bundleId !== undefined) {
    const memberIds = device.members.map((member) => member.deviceId);
    const factoryIds = factoryList && factoryList.map((factoryDevice) => factoryDevice.deviceId);
    let isMemberProtected = factoryIds && memberIds.map((memberId) => factoryIds.includes(memberId));
    const canModifyProtectedDevices = canModifyFactoryState(userPermissions);
    return {
      bundleId: device.bundleId,
      memberIds,
      bundleState: getBundleFactoryState(isMemberProtected, canModifyProtectedDevices),
      memberStates:
        isMemberProtected &&
        isMemberProtected.map((isProtected) => getDeviceFactoryState(isProtected, canModifyProtectedDevices))
    };
  } else {
    const isProtected = factoryList && factoryList.some((factoryDevice) => factoryDevice.deviceId === device.id);
    const canModifyProtectedDevices = canModifyFactoryState(userPermissions);
    return {
      deviceId: device.id,
      state: getDeviceFactoryState(isProtected, canModifyProtectedDevices)
    };
  }
};

export function locationColAccessor(device) {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;
  const location = mainDevice?.properties?.reported?.location;
  const displayString = `${location?.lat || 'N/A'}, ${location?.long || 'N/A'}`;
  return displayString;
}

export const getDeviceAzimuthsAndDowntilts = (device) => {
  const mainDevice = isItBundle(device) ? getMainUnitFromBundleMembers(device?.members) : device;

  const electricalAz = mainDevice?.properties?.reported?.antenna_direction?.beamAzimuth ?? 'Unknown';
  const mechanicalAz = mainDevice?.properties?.reported?.systemOrientation?.Yaw ?? 'Unknown';
  const absoluteAz =
    (electricalAz !== 'Unknown' && mechanicalAz !== 'Unknown' && (electricalAz + mechanicalAz).toFixed(2).toString()) ??
    'Unknown';

  const beamElevation = mainDevice?.properties?.reported?.antenna_direction?.beamElevation ?? null;
  // const pitch = mainDevice?.properties?.reported?.systemOrientation?.Pitch ?? 'Unknown';
  const electricalDowntilt = beamElevation !== null ? beamElevation * -1 : 'Unknown';
  const mechanicalDowntilt = 0; //current requirement to set all mechanical downtilt to zero
  const absoluteDowntilt =
    electricalDowntilt !== 'Unknown' && mechanicalDowntilt !== 'Unknown'
      ? (electricalDowntilt + mechanicalDowntilt)?.toFixed(2)?.toString()
      : 'Unknown';

  return {
    absoluteAz,
    electricalAz,
    mechanicalAz,
    absoluteDowntilt,
    electricalDowntilt,
    mechanicalDowntilt
  };
};

export const updateTabColumnsPreference = (defaultTabCols, currentTabCols, permissions) => {
  if (!currentTabCols || currentTabCols?.length === 0) return defaultTabCols;
  const defaultTabColsObj = {};
  const currentTabColsObj = {};
  defaultTabCols.forEach((col) => {
    defaultTabColsObj[col.id] = col;
  });
  currentTabCols.forEach((col) => {
    currentTabColsObj[col.id] = col;
  });

  defaultTabCols.forEach((col) => {
    if (col.id in currentTabColsObj) {
      if (col.id === 'factory_col' && !canReadFactoryState(permissions)) {
        defaultTabColsObj[col.id].selected = false;
      } else {
        defaultTabColsObj[col.id].selected = currentTabColsObj[col.id].selected;
      }
    }
  });
  let updatedTabCols = Object.values(defaultTabColsObj);
  return updatedTabCols;
};
